import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSettings, FiCopy, FiEye,
  FiMessageSquare, FiPhone, FiMail, FiGlobe, FiSlack,
  FiSend, FiZap, FiCpu, FiDatabase, FiActivity, FiFolder,
  FiFolderPlus, FiMoreHorizontal, FiStar, FiDownload,
  FiTrendingUp, FiUsers, FiShoppingCart, FiHeart, FiCode,
  FiCalendar, FiClock
} from 'react-icons/fi';
import { BiBot, BiBrain } from 'react-icons/bi';
import { BsWhatsapp, BsRobot } from 'react-icons/bs';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchAssistants as fetchAssistantsThunk, addAssistant, updateAssistant, removeAssistant, Assistant } from '../store/slices/assistantSlice';
import { io as socketIOClient, Socket } from 'socket.io-client';
import { getWsBaseUrl, getApiBaseUrl } from '../utils/env';
import CreateAssistantModal from '../components/CreateAssistantModal';
import AIAgentPanel from '../components/AIAgentPanel';
import AssistantChatTest from '../components/AssistantChatTest';
import { api as apiClient } from '../utils/api';

// Using Assistant type from store

interface CompanyFolder {
  id: string;
  name: string;
  color: string;
  assistantCount: number;
  isActive: boolean;
  icon: React.ReactNode;
}

interface AssistantTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  color: string;
  icon: React.ReactNode;
  features: string[];
  popularity: number;
  useCase: string;
  estimatedSetupTime: string;
}

const Assistants: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const assistantsFromStore = useAppSelector(state => state.assistants.assistants) as unknown as Assistant[];
  const assistantsLoading = useAppSelector(state => state.assistants.loading) as unknown as boolean;
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [hoveredFolder, setHoveredFolder] = useState<string | null>(null);
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const [showAgentPanel, setShowAgentPanel] = useState<string | null>(null);
  const [showChatTest, setShowChatTest] = useState<Assistant | null>(null);

  // Company folders derived from assistant.folderId (fallbacks to organizationId)
  const companyFolders: CompanyFolder[] = React.useMemo(() => {
    const foldersMap = new Map<string, CompanyFolder>();
    // Always include All
    foldersMap.set('all', {
      id: 'all',
      name: 'All Assistants',
      color: 'from-gray-500 to-gray-600',
      assistantCount: assistants.length,
      isActive: true,
      icon: <FiDatabase size={20} />
    });

    assistants.forEach(a => {
      const id = a.folderId || 'unknown';
      if (!foldersMap.has(id)) {
        foldersMap.set(id, {
          id,
          name: `Org ${id?.slice(0, 6) || 'Unknown'}`,
          color: 'from-blue-500 to-cyan-500',
          assistantCount: 0,
          isActive: true,
          icon: <FiFolder size={20} />
        });
      }
      const folder = foldersMap.get(id)!;
      folder.assistantCount += 1;
    });

    return Array.from(foldersMap.values());
  }, [assistants]);

  // Assistant templates - will be loaded from API in the future
  const assistantTemplates: AssistantTemplate[] = [];

  const { isAuthenticated } = useAuth();

  // Sync local list with store
  useEffect(() => {
    console.log('Redux assistants updated:', assistantsFromStore?.length, assistantsFromStore);
    setAssistants(assistantsFromStore || []);
  }, [assistantsFromStore]);

  const runFetch = useCallback(() => {
    const status = filterStatus !== 'all' ? filterStatus : undefined;
    const folderId = selectedFolder !== 'all' ? selectedFolder : undefined;
    const search = searchTerm || undefined;
    dispatch(fetchAssistantsThunk({ status, folderId, search }) as any);
  }, [dispatch, filterStatus, selectedFolder, searchTerm]);

  useEffect(() => {
    if (isAuthenticated) {
      runFetch();
    }
  }, [isAuthenticated, runFetch]);

  // Ensure LLM providers include Mistral/OpenAI based on backend config
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/llm/providers');
        console.log('LLM providers:', data?.providers);
      } catch {}
    })();
  }, []);
  // WebSocket subscription for real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;
    // Read Vite env in browser or Jest
    // @ts-ignore
    const viteEnv = (typeof (globalThis as any).import !== 'undefined' && (globalThis as any).import.meta?.env)
      // @ts-ignore
      || (typeof (globalThis as any).importMeta !== 'undefined' && (globalThis as any).importMeta?.env)
      || {};
    const baseUrl = getWsBaseUrl();
    let socket: Socket | null = null;
    try {
      socket = socketIOClient(baseUrl, { transports: ['websocket'], withCredentials: true });
      socket.on('connect', () => {});
      socket.on('assistants-updates', (payload: any) => {
        if (payload?.type === 'assistant.created' && payload.assistant) {
          dispatch(addAssistant(payload.assistant));
        } else if (payload?.type === 'assistant.updated' && payload.assistant) {
          dispatch(updateAssistant(payload.assistant));
        } else if (payload?.type === 'assistant.deleted' && payload.assistantId) {
          dispatch(removeAssistant(payload.assistantId));
        }
      });
    } catch {}
    return () => {
      try { socket?.disconnect(); } catch {}
    };
  }, [dispatch, isAuthenticated]);


  // Refetch on search term changes (debounced)
  useEffect(() => {
    if (!isAuthenticated) return;
    const t = setTimeout(() => runFetch(), 300);
    return () => clearTimeout(t);
  }, [searchTerm, isAuthenticated, runFetch]);

  const channelIcons: { [key: string]: React.ReactNode } = {
    sms: <FiMessageSquare size={16} />,
    whatsapp: <BsWhatsapp size={16} />,
    webchat: <FiGlobe size={16} />,
    phone: <FiPhone size={16} />,
    email: <FiMail size={16} />,
    slack: <FiSlack size={16} />
  };

  const filteredAssistants = useMemo(() => {
    return assistants.filter(assistant => {
    const matchesSearch = assistant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         assistant.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || assistant.status === filterStatus;
    const matchesFolder = selectedFolder === 'all' || assistant.folderId === selectedFolder;
    return matchesSearch && matchesFilter && matchesFolder;
  });
  }, [assistants, searchTerm, filterStatus, selectedFolder]);

  const getFolderAssistants = (folderId: string) => {
    if (folderId === 'all') return assistants;
    return assistants.filter(assistant => assistant.folderId === folderId);
  };

  const handleAssistantCreated = () => {
    setShowCreateModal(false);
    // No need to fetch - Redux already has the assistant from createAssistant.fulfilled
    // The useEffect on line 105-108 syncs Redux state to local state automatically
    toast.success('Assistant created successfully!');
  };

  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.05, 0.15, 0.05],
              rotate: [0, 360],
            }}
            transition={{
              duration: 15 + i * 3,
              repeat: Infinity,
              delay: i * 2,
            }}
          >
            <BsRobot size={30 + i * 8} className="text-purple-500/10" />
          </motion.div>
        ))}
      </div>

      {/* Left Side - Company Folders */}
      <div className="w-1/2 relative z-10 border-r border-gray-700/50">
        <div className="h-full flex flex-col">
          {/* Left Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 border-b border-gray-700/50"
            style={{
              background: 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent mb-2">
              Organizations
            </h1>
            <p className="text-gray-400 text-sm">Browse assistants by company folders</p>
            
            {/* Search */}
            <div className="mt-4">
              <input
                type="text"
                placeholder="Search folders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-gray-800/50 border border-gray-600/50 rounded-lg text-white focus:outline-none focus:border-cyan-500 transition-colors"
              />
            </div>
          </motion.div>

          {/* Folder List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {companyFolders.map((folder, index) => (
              <motion.div
                key={folder.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  setSelectedFolder(folder.id);
                  setTimeout(runFetch, 0);
                }}
                onHoverStart={() => setHoveredFolder(folder.id)}
                onHoverEnd={() => setHoveredFolder(null)}
                className={`relative p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                  selectedFolder === folder.id
                    ? 'shadow-lg border border-cyan-500/30'
                    : 'border border-gray-700/30 hover:border-gray-600/50'
                }`}
                style={{
                  background: selectedFolder === folder.id 
                    ? `linear-gradient(135deg, rgba(6, 182, 212, 0.15), rgba(147, 51, 234, 0.1))`
                    : 'rgba(30, 41, 59, 0.4)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* Folder Header */}
                <div className="flex items-center gap-3 mb-3">
                  <motion.div
                    animate={hoveredFolder === folder.id ? { rotate: 5 } : { rotate: 0 }}
                    className={`p-2 rounded-lg bg-gradient-to-br ${folder.color} opacity-90`}
                  >
                    {selectedFolder === folder.id ? <FiFolderPlus size={18} className="text-white" /> : folder.icon}
                  </motion.div>
                  <div className="flex-1">
                    <h3 className={`font-semibold transition-colors ${
                      selectedFolder === folder.id ? 'text-cyan-300' : 'text-white'
                    }`}>
                      {folder.name}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {folder.assistantCount} assistant{folder.assistantCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <FiMoreHorizontal size={16} className="text-gray-500" />
                </div>

                {/* Assistant Preview Cards */}
                <div className="space-y-2">
                  {getFolderAssistants(folder.id).length > 0 ? (
                    <>
                      {/* Show all assistants with their IDs */}
                      {getFolderAssistants(folder.id)
                        .slice(0, 3)
                        .map((assistant, index) => (
                          <motion.div
                            key={assistant.id}
                            whileHover={{ x: 5 }}
                            className="flex flex-col gap-1 p-2 rounded-lg bg-gray-800/30 border border-gray-700/20 cursor-default"
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${
                                assistant.status === 'active' ? 'bg-green-500' :
                                assistant.status === 'training' ? 'bg-yellow-500' : 'bg-gray-500'
                              }`} />
                              <span className="text-sm text-gray-300 flex-1 truncate">
                                {assistant.name}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500 font-mono pl-4 truncate">
                              {assistant.id?.slice(0, 8) || 'N/A'}...
                            </span>
                          </motion.div>
                        ))}
                      {getFolderAssistants(folder.id).length > 3 && (
                        <div className="text-xs text-gray-500 text-center py-1">
                          +{getFolderAssistants(folder.id).length - 3} more
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-xs text-gray-500 text-center py-2">
                      No assistants yet
                    </div>
                  )}
                </div>

                {/* Active Animation */}
                {selectedFolder === folder.id && (
                  <motion.div
                    layoutId="activeFolder"
                    className="absolute inset-0 rounded-xl border-2 border-cyan-500/40"
                    initial={false}
                    transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                  />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Side - Templates & Assistants */}
      <div className="w-1/2 relative z-10">
        <div className="h-full flex flex-col">
          {/* Right Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-6 border-b border-gray-700/50"
            style={{
              background: 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(20px)',
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 bg-clip-text text-transparent mb-1">
                  Templates & Assistants
                </h1>
                <p className="text-gray-400 text-sm">
                  {selectedFolder === 'all' ? 'All available templates and assistants' : 
                   `Templates and assistants for ${companyFolders.find(f => f.id === selectedFolder)?.name}`}
                </p>
              </div>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold flex items-center gap-2"
              >
                <FiPlus size={16} />
                New
              </motion.button>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
              {['all', 'active', 'inactive', 'training', 'creating'].map((status) => (
                <motion.button
                  key={status}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setFilterStatus(status);
                    setTimeout(runFetch, 0);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-all ${
                    filterStatus === status
                      ? 'bg-gradient-to-r from-purple-500/80 to-pink-500/80 text-white'
                      : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white'
                  }`}
                >
                  {status}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Templates and Assistants Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Templates Section - Hidden when no templates */}
            <div className="p-4">
              {assistantTemplates.length > 0 && (
                <>
                  <motion.h2
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-lg font-semibold text-white mb-4 flex items-center gap-2"
                  >
                    <FiStar className="text-yellow-400" />
                    Popular Templates
                  </motion.h2>
                  
                  <div className="space-y-3 mb-6">
                    {assistantTemplates.slice(0, 3).map((template, index) => (
                      <motion.div
                        key={template.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onHoverStart={() => setHoveredTemplate(template.id)}
                        onHoverEnd={() => setHoveredTemplate(null)}
                        className="relative p-4 rounded-xl cursor-pointer transition-all duration-300 border border-gray-700/30 hover:border-gray-600/50"
                        style={{
                          background: hoveredTemplate === template.id 
                            ? `linear-gradient(135deg, rgba(147, 51, 234, 0.15), rgba(236, 72, 153, 0.1))`
                            : 'rgba(30, 41, 59, 0.4)',
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <motion.div
                            animate={hoveredTemplate === template.id ? { rotate: 10, scale: 1.1 } : {}}
                            className={`p-2 rounded-lg bg-gradient-to-br ${template.color}`}
                          >
                            {template.icon}
                          </motion.div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-white truncate">{template.name}</h3>
                              <div className="flex items-center gap-1 text-xs text-green-400">
                                <FiTrendingUp size={12} />
                                {template.popularity}%
                              </div>
                            </div>
                            <p className="text-sm text-gray-400 mb-2 line-clamp-2">{template.description}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-purple-300 bg-purple-500/20 px-2 py-1 rounded">
                                {template.category}
                              </span>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <FiClock size={12} />
                                {template.estimatedSetupTime}
                              </div>
                            </div>
                          </div>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            className="p-2 rounded-lg bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors"
                          >
                            <FiDownload size={14} />
                          </motion.button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </>
              )}

              {/* Current Assistants */}
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-lg font-semibold text-white mb-4 flex items-center gap-2"
              >
                <BiBot className="text-cyan-400" />
                Your Assistants
                <span className="text-sm text-gray-500">({filteredAssistants.length})</span>
              </motion.h2>

              <div className="space-y-3">
                {assistantsLoading ? (
                  <div className="text-center py-12">
                    <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <div className="text-gray-400">Loading assistants...</div>
                  </div>
                ) : filteredAssistants.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-12"
                  >
                    <BiBot className="w-16 h-16 mx-auto text-gray-600 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-400 mb-2">No assistants yet</h3>
                    <p className="text-gray-500 mb-6">Create your first AI assistant to get started</p>
                    <motion.button
                      onClick={() => setShowCreateModal(true)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-300"
                    >
                      <FiPlus className="inline mr-2" />
                      Create Assistant
                    </motion.button>
                  </motion.div>
                ) : (
                  <AnimatePresence>
                    {filteredAssistants.map((assistant, index) => (
                    <motion.div
                      key={assistant.id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: 0.6 + index * 0.05 }}
                      whileHover={{ scale: 1.02 }}
                      className="relative p-4 rounded-xl border border-white/20 hover:border-white/40 transition-all duration-300 cursor-pointer hover:shadow-lg hover:shadow-white/10"
                      style={{
                        background: 'rgba(30, 41, 59, 0.4)',
                        backdropFilter: 'blur(10px)',
                      }}
                      onClick={() => navigate(`/assistants/${assistant.id}/edit`)}
                    >

                      <div className="flex items-start gap-3">
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.5 }}
                          className={`p-2 rounded-lg bg-gradient-to-br ${assistant.color}`}
                        >
                          <BiBot size={20} className="text-white" />
                        </motion.div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-white truncate">{assistant.name}</h3>
                            <div className={`w-2 h-2 rounded-full ${
                              assistant.status === 'active' ? 'bg-green-500' :
                              assistant.status === 'training' ? 'bg-yellow-500' : 'bg-gray-500'
                            }`} />
                          </div>
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs text-gray-500 font-mono bg-gray-800/50 px-2 py-0.5 rounded">
                              ID: {assistant.id}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mb-3 line-clamp-2">{assistant.description}</p>
                          
                          {/* Stats Row */}
                          <div className="flex items-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <FiMessageSquare size={12} className="text-blue-400" />
                              <span className="text-gray-300">{(assistant.messagesHandled || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiTrendingUp size={12} className="text-green-400" />
                              <span className="text-gray-300">{assistant.successRate || 0}%</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FiClock size={12} className="text-purple-400" />
                              <span className="text-gray-300">{assistant.responseTime || 0}s</span>
                            </div>
                          </div>
                          
                          {/* Channels */}
                          <div className="flex gap-1 mt-2">
                            {(assistant.channels || []).slice(0, 4).map((channel) => (
                              <div key={channel} className="p-1 bg-gray-800/50 rounded text-gray-400">
                                {channelIcons[channel] || <FiGlobe size={12} />}
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAgentPanel(assistant.id);
                            }}
                            className="p-2 rounded-lg bg-cyan-600/20 text-cyan-400 hover:bg-cyan-600/30 hover:text-cyan-300 transition-all border border-cyan-600/30"
                            title="View AI Agents"
                          >
                            <FiEye size={18} />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowChatTest(assistant);
                            }}
                            className="p-2 rounded-lg bg-green-600/20 text-green-400 hover:bg-green-600/30 hover:text-green-300 transition-all border border-green-600/30"
                            title="Test Chat"
                          >
                            <FiMessageSquare size={18} />
                          </motion.button>
                        </div>
                      </div>
                    </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Assistant Modal */}
      <CreateAssistantModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleAssistantCreated}
      />

      {/* AI Agent Panel Modal */}
      {showAgentPanel && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gray-900 rounded-xl p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-white">AI Agents</h2>
              <button
                onClick={() => setShowAgentPanel(null)}
                className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            <AIAgentPanel assistantId={showAgentPanel} />
          </motion.div>
        </div>
      )}

      {/* Chat Test Modal */}
      <AnimatePresence>
        {showChatTest && (
          <AssistantChatTest
            assistantData={showChatTest}
            onClose={() => setShowChatTest(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Assistants;